import type { AppType } from '@squadcore/api/app'
import { hc, type ClientResponse } from 'hono/client'
import type { SuccessStatusCode } from 'hono/utils/http-status'

/** A failed API call. `message` is German UI copy from the server (or a network fallback). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message)
  }
}

let onUnauthorized: () => void = () => {}
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn
}

const client = hc<AppType>('/', {
  headers: { 'X-SquadCore': '1' },
  init: { credentials: 'same-origin' },
})

/** Typed API client: api.teams[':teamId'].matches.$get({ param, query }) */
export const api = client.api

type SuccessBody<R> =
  R extends ClientResponse<infer T, infer S, infer _F> ? (S extends SuccessStatusCode ? T : never) : never

/** Await a client call, return the success JSON or throw ApiError. */
export async function unwrap<R extends ClientResponse<unknown, number, string>>(
  call: Promise<R>,
): Promise<SuccessBody<R>> {
  let res: R
  try {
    res = await call
  } catch {
    throw new ApiError(0, 'network', 'Keine Verbindung. Prüfe dein Netz und versuch es erneut.')
  }
  if (res.ok) return (await res.json()) as SuccessBody<R>
  let body: { error?: { code?: string; message?: string; fields?: Record<string, string> } } = {}
  try {
    body = (await res.json()) as typeof body
  } catch {
    // proxy error page or empty body
  }
  if (res.status === 401) onUnauthorized()
  throw new ApiError(
    res.status,
    body.error?.code ?? 'http_' + res.status,
    body.error?.message ??
      (res.status >= 500
        ? 'Der Server antwortet gerade nicht. Versuch es gleich noch einmal.'
        : 'Die Anfrage ist fehlgeschlagen.'),
    body.error?.fields,
  )
}

/** For uploads and binary downloads that the RPC client doesn't cover. */
export async function rawFetch(path: string, init: RequestInit = {}) {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'X-SquadCore': '1', ...init.headers },
  })
  if (!res.ok) {
    let msg = 'Die Anfrage ist fehlgeschlagen.'
    try {
      msg = ((await res.json()) as { error?: { message?: string } }).error?.message ?? msg
    } catch {
      // not JSON
    }
    if (res.status === 401) onUnauthorized()
    throw new ApiError(res.status, 'http_' + res.status, msg)
  }
  return res
}
